import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError, type U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { doInitLU, luHandleAppiError, type U3104InitLU } from "./U3104Service";
import { U3104ContextProvider } from "./U3104Context";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

export const NS = "u3104";

export const U3104ROLES = {
  READ: "U3104:READ",
  ADD: "U3104:ADD",
  EDIT: "U3104:EDIT",
  DELETE: "U3104:DELETE",
  AUDITS: "U3104:AUDITS",
} as const;
export type U3104ROLES = (typeof U3104ROLES)[keyof typeof U3104ROLES];

const defaultInitLU: U3104InitLU = {
  launchSearch: false,
  hideSearch: true,
  nbRecordPerPage: 25,
  logicalUnitCode: "U3104",
};

const U3104: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3104InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(["u3104"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3104InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3104InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        logicalUnitCode: initLU.logicalUnitCode ?? "U3104",
      });
      setInitLUReady(true);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- aligné U3110
    onError: ({ error }: { error: any }) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps -- aligné U3110

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t("title", { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3104ContextProvider initLU={initLUData}>
      <Outlet />
    </U3104ContextProvider>
  );
};

export default U3104;
